import { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import MagazineCard from './MagazineCard';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import { API_ENDPOINTS } from '../../config/constants';
import { POPULATE_PARAMS } from '../../config/constants';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

const MagazineList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 12;

    const { data: magazines, loading, error, meta } = useFetch(
        API_ENDPOINTS.MAGAZINES,
        POPULATE_PARAMS.WITH_IMAGES_SORTED_DESC(currentPage, pageSize)
    );

    const pagination = meta?.pagination;
    const totalPages = pagination?.pageCount || 1;

    // Smart pagination - show current page with context and ellipsis
    const getPageNumbers = () => {
        const pages = [];
        const showEllipsisThreshold = 7;

        if (totalPages <= showEllipsisThreshold) {
            // Show all pages if total is small
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        // Always show first page
        pages.push(1);

        // Calculate range around current page
        let startPage = Math.max(2, currentPage - 2);
        let endPage = Math.min(totalPages - 1, currentPage + 2);

        // Adjust range if at the beginning
        if (currentPage <= 3) {
            startPage = 2;
            endPage = 5;
        }

        // Adjust range if at the end
        if (currentPage >= totalPages - 2) {
            startPage = totalPages - 4;
            endPage = totalPages - 1;
        }

        // Add ellipsis after first page if needed
        if (startPage > 2) {
            pages.push('...');
        }

        // Add pages in range
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        // Add ellipsis before last page if needed
        if (endPage < totalPages - 1) {
            pages.push('...');
        }

        // Always show last page
        if (totalPages > 1) {
            pages.push(totalPages);
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading) return <Loading />;
    if (error) return <ErrorMessage message={error} />;
    if (!magazines || magazines.length === 0) {
        return (
            <p className="text-center text-gray-400 text-lg sm:text-xl mt-12 sm:mt-16 px-4">
                İçerik bulunamadı.
            </p>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8 lg:mb-10 text-gray-800">
                Dergiler
            </h1>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
                {magazines.map((magazine) => (
                    <MagazineCard key={magazine.slug} magazine={magazine} />
                ))}
            </div>

            {/* Responsive Pagination */}
            {totalPages > 1 && (
                <nav
                    className="flex flex-col items-center gap-3 sm:gap-4 mt-10 sm:mt-12 lg:mt-16"
                    aria-label="Pagination"
                >
                    <div className="flex justify-center items-center gap-1 sm:gap-2">
                        {/* Previous Button */}
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="p-1.5 sm:p-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all disabled:hover:bg-white flex-shrink-0"
                            aria-label="Önceki sayfa"
                        >
                            <MdChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>

                        {/* Page Numbers */}
                        <div className="flex gap-0.5 sm:gap-1 md:gap-2 overflow-x-auto scrollbar-hide max-w-[calc(100vw-120px)] sm:max-w-none">
                            {pageNumbers.map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <span
                                            key={`dots-${index}`}
                                            className="px-1 sm:px-2 py-1.5 sm:py-2 text-gray-500 select-none text-sm sm:text-base flex-shrink-0"
                                        >
                                            ...
                                        </span>
                                    );
                                }

                                return (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`min-w-[32px] sm:min-w-[40px] px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg border font-medium text-sm sm:text-base transition-all flex-shrink-0 ${currentPage === page
                                            ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                                            }`}
                                        aria-label={`Sayfa ${page}`}
                                        aria-current={currentPage === page ? 'page' : undefined}
                                    >
                                        {page}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Next Button */}
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="p-1.5 sm:p-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all disabled:hover:bg-white flex-shrink-0"
                            aria-label="Sonraki sayfa"
                        >
                            <MdChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                    </div>

                    {/* Page Info */}
                    <div className="text-center text-xs sm:text-sm text-gray-600">
                        Sayfa {currentPage} / {totalPages}
                    </div>
                </nav>
            )}
        </div>
    );
};

export default MagazineList;